// src/components/Toast.js
// 화면 어디서든 보이는 토스트 알림. App 최상단에 <ToastProvider>로 감싸고,
// 각 화면에서 const toast = useToast(); toast.error('메시지') 형태로 사용한다.
import React, { createContext, useCallback, useContext, useState } from 'react';
import '../styles/Toast.css';

const ToastContext = createContext(null);

const AUTO_DISMISS_MS = 3500;

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const remove = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const show = useCallback((message, type = 'info') => {
        if (!message) return;
        // 같은 메시지가 연속으로 뜨는 것을 막아 화면이 쌓이지 않게 한다
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev.filter(t => t.message !== message), { id, message, type }]);
        setTimeout(() => remove(id), AUTO_DISMISS_MS);
    }, [remove]);

    const value = {
        show,
        success: useCallback((message) => show(message, 'success'), [show]),
        error: useCallback((message) => show(message, 'error'), [show]),
        info: useCallback((message) => show(message, 'info'), [show])
    };

    return (
        <ToastContext.Provider value={value}>
            {children}
            <div className="toast-container" role="status" aria-live="polite">
                {toasts.map(toast => (
                    <div
                        key={toast.id}
                        className={`toast toast-${toast.type}`}
                        onClick={() => remove(toast.id)}
                    >
                        <span className="toast-icon">
                            {toast.type === 'success' ? '✅' : toast.type === 'error' ? '⚠️' : 'ℹ️'}
                        </span>
                        <span className="toast-message">{toast.message}</span>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast는 ToastProvider 안에서만 사용할 수 있습니다.');
    }
    return context;
};
